import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIf } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { PaButtonComponent } from '../pa-button/pa-button.component';
import { PaDatepickerRangeComponent } from '../pa-datepicker-range/pa-datepicker-range.component';
import { ValueAccessorDirective } from '../../directives/value-accessor.directive';
import { CustomErrorMessage } from '../../models/custom-error-messages.model';
import { SelectOption } from 'src/app/core/models/dictionary';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  addDays,
  diffInDays,
  formatDdMmYyDate,
  getDatesForPeriod,
  getEndDateForPeriod,
  isCurrentPeriodId,
  matchesCurrentPeriod,
  matchesPeriodDuration,
  parseDdMmYyDate,
  shiftDates,
} from '../../helpers/date-period.helper';
import { PeriodDateRangeId } from '../../models';

@Component({
  standalone: true,
  selector: 'pa-datepicker-range-period',
  imports: [CommonModule, ReactiveFormsModule, NgIf, TooltipModule, PaButtonComponent, PaDatepickerRangeComponent],
  templateUrl: './pa-datepicker-range-period.component.html',
  styleUrls: ['./pa-datepicker-range-period.component.scss'],
  hostDirectives: [ValueAccessorDirective],
})
export class PaDatepickerRangePeriodComponent implements OnInit {
  @Input() label?: string;
  @Input() hint?: string;
  @Input() tooltip?: string;
  @Input() placeholder?: string;
  @Input() customErrorMessages: CustomErrorMessage | null = null;

  private readonly defaultPeriodOptions = [
    { id: PeriodDateRangeId.WEEK, name: 'Неделя' },
    { id: PeriodDateRangeId.TWO_WEEKS, name: '2 недели' },
    { id: PeriodDateRangeId.MONTH, name: 'Месяц' },
  ];

  @Input() periodOptions: SelectOption<PeriodDateRangeId>[] = this.defaultPeriodOptions;

  readonly valueAccessor = inject(ValueAccessorDirective<string[]>);

  private readonly destroyRef = inject(DestroyRef);

  /**
   * Опции для выбора периода
   */
  readonly periodControl = new FormControl<string | null>(null);

  // Внутри используем Date[] для корректной подсветки выбранного range в popup.
  readonly innerControl = new FormControl<Date[] | null>(null);

  isRequired = false;

  private isProgrammaticDateUpdate = false;
  private isSyncingFromOuter = false;
  private isSyncingFromInner = false;
  private isProgrammaticPeriodUpdate = false;
  private lastProgrammaticDates: string[] | null = null;

  ngOnInit(): void {
    const outerControl = this.valueAccessor.control;
    if (!outerControl) return;

    this.isRequired = outerControl.hasValidator(Validators.required);

    // Копируем валидаторы, чтобы требуемые/недопустимые состояния работали одинаково.
    // hasValidator() проверяет _rawValidators по ссылке, поэтому требуется
    // должен быть добавлен явно после копирования составленного fn валидатора.
    this.innerControl.setValidators(outerControl.validator);
    if (outerControl.hasValidator(Validators.required)) {
      this.innerControl.addValidators(Validators.required);
    }
    this.innerControl.updateValueAndValidity({ emitEvent: false });

    const outerDates = this.normalizeDatesValue(outerControl.value);
    this.isProgrammaticDateUpdate = true;
    this.setInnerFromOuterDates(outerDates);
    this.isProgrammaticDateUpdate = false;

    this.syncPeriodFromDates(outerDates);

    outerControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (this.isProgrammaticDateUpdate || this.isSyncingFromInner) return;

      const dates = this.normalizeDatesValue(value);
      this.syncPeriodFromDates(dates);

      this.isSyncingFromOuter = true;
      this.setInnerFromOuterDates(dates);
      this.isSyncingFromOuter = false;
    });

    this.innerControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (this.isProgrammaticDateUpdate || this.isSyncingFromOuter) return;

      const dates = this.normalizeDatesValue(value);

      this.isSyncingFromInner = true;
      outerControl.setValue(dates as unknown as string[]);
      this.isSyncingFromInner = false;

      this.clearPeriodSelectionIfNeeded();
    });

    this.periodControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((periodId) => {
      if (this.isProgrammaticPeriodUpdate) return;
      if (!periodId) return;

      const currentDates = this.normalizeDatesValue(this.innerControl.value);
      const startStr = currentDates?.[0];
      const startDate = startStr ? parseDdMmYyDate(startStr) : null;

      let dates: string[] | null;
      if (!isCurrentPeriodId(periodId) && startDate && startStr) {
        const endStr = getEndDateForPeriod(periodId, startDate);
        dates = endStr ? [startStr, endStr] : null;
      } else {
        dates = getDatesForPeriod(periodId, new Date());
      }

      this.isProgrammaticDateUpdate = true;
      this.setDates(dates);
      this.isProgrammaticDateUpdate = false;
    });
  }

  get resolvedPeriodOptions(): SelectOption<PeriodDateRangeId>[] {
    if (this.periodOptions && this.periodOptions.length > 0) {
      return this.periodOptions;
    }

    return this.defaultPeriodOptions;
  }

  get canShift(): boolean {
    return !!this.normalizeDatesValue(this.innerControl.value);
  }

  handlePeriodSelect(id: string | number): void {
    const strId = String(id);
    if (this.periodControl.value === strId) return;
    this.periodControl.setValue(strId);
  }

  handleShift(dir: -1 | 1): void {
    const dates = this.normalizeDatesValue(this.innerControl.value);
    if (!dates) return;

    const start = parseDdMmYyDate(dates[0]);
    if (!start) return;

    const periodId = this.periodControl.value;

    const shifted = periodId
      ? shiftDates(periodId, start, dir)
      : this.isFullCalendarMonth(start, dates[1])
        ? shiftDates(PeriodDateRangeId.MONTH, start, dir)
        : this.shiftByCustomRange(start, dates[1], dir);

    this.isProgrammaticDateUpdate = true;
    this.setDates(shifted);
    this.isProgrammaticDateUpdate = false;

    this.syncPeriodFromDates(shifted);
  }

  private isFullCalendarMonth(start: Date, endStr: string): boolean {
    const end = parseDdMmYyDate(endStr);
    if (!end) return false;
    const lastDay = new Date(
      start.getFullYear(),
      start.getMonth() + 1,
      0,
    ).getDate();
    return (
      start.getDate() === 1 &&
      end.getDate() === lastDay &&
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()
    );
  }

  private shiftByCustomRange(start: Date, endStr: string, dir: -1 | 1): string[] | null {
    const end = parseDdMmYyDate(endStr);
    if (!end) return null;

    const duration = diffInDays(start, end) + 1;
    const newStart = addDays(start, dir * duration);
    const newEnd = addDays(end, dir * duration);
    return [formatDdMmYyDate(newStart), formatDdMmYyDate(newEnd)];
  }

  private setDates(dates: string[] | null): void {
    const outerControl = this.valueAccessor.control;
    if (!outerControl) return;

    this.isProgrammaticDateUpdate = true;
    this.isProgrammaticPeriodUpdate = true;

    this.lastProgrammaticDates = dates ? [...dates] : null;
    outerControl.setValue(dates as unknown as string[]);
    this.setInnerFromOuterDates(dates);

    this.isProgrammaticPeriodUpdate = false;
    this.isProgrammaticDateUpdate = false;
  }

  private setInnerFromOuterDates(dates: string[] | null): void {
    if (!dates) {
      this.innerControl.setValue(null, { emitEvent: false });
      return;
    }

    const start = dates[0];
    const end = dates[1];
    if (!start || !end) {
      this.innerControl.setValue(null, { emitEvent: false });
      return;
    }

    const startDate = parseDdMmYyDate(start);
    const endDate = parseDdMmYyDate(end);
    if (!startDate || !endDate) {
      this.innerControl.setValue(null, { emitEvent: false });
      return;
    }

    this.innerControl.setValue([startDate, endDate], { emitEvent: false });
  }

  private findMatchingPeriod(dates: string[] | null): PeriodDateRangeId | null {
    if (!dates) return null;

    const start = parseDdMmYyDate(dates[0]);
    const end = parseDdMmYyDate(dates[1]);
    if (!start || !end) return null;

    const sortedOptions = [...this.resolvedPeriodOptions].sort((a, b) => {
      const aIsCurrent = isCurrentPeriodId(String(a.id));
      const bIsCurrent = isCurrentPeriodId(String(b.id));
      if (aIsCurrent === bIsCurrent) return 0;
      return aIsCurrent ? -1 : 1;
    });

    for (const option of sortedOptions) {
      const matches = isCurrentPeriodId(option.id)
        ? matchesCurrentPeriod(option.id, start, end)
        : matchesPeriodDuration(option.id, start, end);
      if (matches) return option.id as PeriodDateRangeId;
    }

    return null;
  }

  private clearPeriodSelectionIfNeeded(): void {
    const currentDates = this.normalizeDatesValue(this.innerControl.value);
    const matched = this.findMatchingPeriod(currentDates);

    if (
      this.areDatesEqual(currentDates, this.lastProgrammaticDates) &&
      this.periodControl.value === matched
    ) {
      return;
    }

    if (matched !== null) {
      this.setPeriodControl(matched);
      return;
    }

    this.periodControl.setValue(null, { emitEvent: false });
  }

  private areDatesEqual(a: string[] | null, b: string[] | null): boolean {
    if (a === null || b === null) return a === b;
    if (a.length !== b.length) return false;
    if (a.length !== 2) return false;
    return a[0] === b[0] && a[1] === b[1];
  }

  private syncPeriodFromDates(dates: string[] | null): void {
    this.setPeriodControl(this.findMatchingPeriod(dates));
  }

  private setPeriodControl(periodId: PeriodDateRangeId | null): void {
    this.isProgrammaticPeriodUpdate = true;
    this.periodControl.setValue(periodId, { emitEvent: false });
    this.isProgrammaticPeriodUpdate = false;
  }

  private normalizeDatesValue(value: unknown): string[] | null {
    if (value === null || value === undefined) return null;

    if (Array.isArray(value)) {
      const start = value[0];
      const end = value[1];
      if (typeof start === 'string' && typeof end === 'string') {
        return [start, end];
      }

      if (start instanceof Date && end instanceof Date) {
        return [formatDdMmYyDate(start), formatDdMmYyDate(end)];
      }
      return null;
    }

    return null;
  }
}
